import _ from 'lodash';
import classnames from 'classnames';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { COLORS_PROP_TYPE } from 'eventbrite_design_system/color/constants';
import { FeatureInfo } from './_internal/features';
import Heading from './Heading';
import Template from './_internal/Template';
import Image from './Image';
import {
    FEATURES_PROP_TYPES,
    HEADING_PROPS,
    TEMPLATE_PROP_TYPES,
} from '../constants/componentPropTypes';

import './threeColumnFeatures.scss';

const NUMBER_OF_COLUMNS = 3;
const IMAGE_HEIGHT = 350;

const _getFeatureListItems = (
    features,
    imagePaths,
    useGrid,
    textColor,
    headingColor,
    iconColor,
) =>
    _.range(NUMBER_OF_COLUMNS).map((index) => {
        const { heading, iconType, description } = features[index];
        let image;

        if (imagePaths && imagePaths[index]) {
            image = (
                <Image
                    imagePath={imagePaths[index]}
                    altDescription={heading}
                    isFullWidth={true}
                    height={IMAGE_HEIGHT}
                />
            );
        }

        let secondaryFeatureInfo;
        const secondaryFeature = features[index + NUMBER_OF_COLUMNS];

        if (secondaryFeature) {
            secondaryFeatureInfo = <FeatureInfo {...secondaryFeature} />;
        }

        const className = classnames('eds-l-pad-all-2', {
            'eds-g-cell eds-g-cell-1-3': useGrid,
        });

        return (
            <div key={`${heading}`} className={className}>
                {image}
                <FeatureInfo
                    heading={heading}
                    iconType={iconType}
                    description={description}
                    headingColor={headingColor}
                    textColor={textColor}
                    iconColor={iconColor}
                />
                {secondaryFeatureInfo}
            </div>
        );
    });

export default class ThreeColumnFeatures extends PureComponent {
    static propTypes = {
        ...TEMPLATE_PROP_TYPES,
        ...FEATURES_PROP_TYPES,
        /*
        List of image paths to accompany features
        */
        imagePaths: PropTypes.arrayOf(PropTypes.string),
        /*
        Optional background color for the whole component
        */
        bgColor: COLORS_PROP_TYPE,
        /*
        Optional text color for the heading
        */
        headingColor: COLORS_PROP_TYPE,
        /*
        Optional text color for the features
        */
        textColor: COLORS_PROP_TYPE,
        /*
        Adds extra bottom padding for overflow container
        */
        addExtraPadding: PropTypes.bool,
        /*
        Adds marketing max width class to the features list
        */
        hasMarketingContainer: PropTypes.bool,
    };

    render() {
        const {
            features,
            imagePaths,
            pageName,
            bgColor,
            headingColor,
            textColor,
            iconColor,
            addExtraPadding,
            hasMarketingContainer,
            ...props
        } = this.props;

        if (!features) {
            return null;
        }

        const largeScreenFeatureList = _getFeatureListItems(
            features,
            imagePaths,
            true,
            textColor,
            headingColor,
            iconColor,
        );
        const smallScreenlargeScreenFeatureList = _getFeatureListItems(
            features,
            imagePaths,
            false,
            textColor,
            headingColor,
            iconColor,
        );
        const headingProps = _.pick(props, HEADING_PROPS);

        const containerClassName = classnames(`eds-bg-color--${bgColor}`, {
            'eds-l-pad-bot-4 features-container--overflow': addExtraPadding,
        });

        const largeScreenContainerClassName = classnames({
            'marketing-container__max-width': hasMarketingContainer,
        });

        return (
            <div className={containerClassName}>
                <Template
                    hasPadding={true}
                    showOnlyLargeScreen={true}
                    useBaseline={true}
                    bgColor={bgColor}
                >
                    <div className="eds-g-cell eds-g-cell-8-12 eds-g-offset-2-12">
                        <Heading
                            {...headingProps}
                            pageName={pageName}
                            useAltHeading={true}
                            bgColor={bgColor}
                            headingColor={headingColor}
                            textColor={textColor}
                        />
                    </div>
                    <div className={largeScreenContainerClassName}>
                        {largeScreenFeatureList}
                    </div>
                </Template>
                <Template
                    hasPadding={true}
                    showOnlySmallScreen={true}
                    bgColor={bgColor}
                >
                    <Heading
                        {...headingProps}
                        pageName={pageName}
                        useAltHeading={true}
                        bgColor={bgColor}
                        headingColor={headingColor}
                        textColor={textColor}
                        alignCenter={true}
                    />
                    {smallScreenlargeScreenFeatureList}
                </Template>
            </div>
        );
    }
}
